import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {ScheduleCallStrategy} from "~/domainModels/scheduleCall/scheduleCallStrategy";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getCurrentIsoTimestamp} from "@vesta/global-common-typescript/utilities/utilities";
import {amaronScheduleCallInlineAction} from "~/utilities/apis/amaronActionsApi";
import {duluxScheduleCallInlineAction} from "~/utilities/apis/duluxActionsApi";
import {createEvent, type EventContainerWithEvents} from "~/utilities/apis/api";
import {updateEventContainer} from "~/utilities/apis/eventContainersApi";
import {omitAmaronCallEventUtmFields} from "~/utilities/workspaceSpecific/amaronCallSubmit";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {getEventContainerOrganizationalUnitColumnId} from "~/utilities/ottopilotSemanticColumns";

export async function submitScheduleCall({
    strategy,
    callTemplate,
    journeyTemplate,
    journey,
    callDate,
    timeSlot,
    workspaceUserId,
    workspaceUserTemplateId,
    accessToken,
    intellsysBaseUrl,
    workspaceId,
}: {
    strategy: ScheduleCallStrategy;
    callTemplate: OttopilotEventTemplate;
    journeyTemplate: EventContainerTemplate;
    journey: EventContainerWithEvents;
    callDate: Date;
    timeSlot: string;
    workspaceUserId: Uuid;
    workspaceUserTemplateId: Uuid;
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Uuid> {
    const scheduledOnIso = strategy.combineDateAndTimeSlot(callDate, timeSlot);

    if (strategy.usesAmaronBackendOrchestration === true) {
        const scheduleCallResult = await amaronScheduleCallInlineAction({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            request: {
                workspaceId: workspaceId,
                eventContainerId: journey.id,
                scheduledOnIso: scheduledOnIso,
            },
        });
        if (scheduleCallResult.success !== true) {
            throw scheduleCallResult.error;
        }
        if (scheduleCallResult.data.callEventId === null || scheduleCallResult.data.callEventId === undefined) {
            throw new Error("Schedule call did not return a call event id.");
        }
        return scheduleCallResult.data.callEventId;
    }

    if (strategy.usesDuluxBackendOrchestration === true) {
        const scheduleCallResult = await duluxScheduleCallInlineAction({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            request: {
                workspaceId: workspaceId,
                eventContainerId: journey.id,
                scheduledOnIso: scheduledOnIso,
            },
        });
        if (scheduleCallResult.success !== true) {
            throw scheduleCallResult.error;
        }
        if (scheduleCallResult.data.callEventId === undefined) {
            throw new Error("Schedule call did not return a call event id.");
        }
        return scheduleCallResult.data.callEventId;
    }

    const now = getCurrentIsoTimestamp();
    const {journeyUpdate, eventAdditionalColumnValues} = strategy.buildSubmitPayload({
        journey,
        journeyTemplate,
        scheduledOnIso,
        workspaceUserId,
        workspaceUserTemplateId,
        now,
    });

    if (journeyUpdate !== null) {
        const organizationalUnitColumnId = getEventContainerOrganizationalUnitColumnId(journeyTemplate);
        const updateEventContainerResult = await updateEventContainer({
            accessToken,
            intellsysBaseUrl,
            workspaceId,
            id: journey.id,
            templateId: journey.templateId,
            data: {
                ...(journeyUpdate.ouId === undefined ? {} : {[organizationalUnitColumnId]: journeyUpdate.ouId}),
                ...journeyUpdate.additionalColumnValues,
            },
        });
        if (updateEventContainerResult.success !== true) {
            throw updateEventContainerResult.error;
        }
    }

    const createEventResult = await createEvent({
        accessToken,
        intellsysBaseUrl,
        workspaceId,
        event: {
            templateId: callTemplate.id,
            eventContainerId: journey.id,
            additionalColumnValues: {
                ...omitAmaronCallEventUtmFields(eventAdditionalColumnValues),
                [callTemplate.configuration.dispositionColumnConfig.columnId]: callTemplate.configuration.dispositionColumnConfig.defaultValue,
            },
        },
    });
    if (createEventResult.success !== true) {
        throw createEventResult.error;
    }

    if (strategy.runAfterSuccess !== undefined) {
        await strategy.runAfterSuccess({
            journey,
            accessToken,
            intellsysBaseUrl,
            workspaceId,
        });
    }

    return createEventResult.data;
}

/** @deprecated Use `submitScheduleCall` */
export const submitListScheduleCall = submitScheduleCall;
